import cn from "classnames";
import { Children, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import {
  GroupedChildrenProps,
  WithGroupedChildrenComponent,
  withGroupedChildren,
} from "react-grouped-children";

import { Oriented } from "@/components/Oriented/Oriented";
import { TooltipIconColor, TooltipWithIcon } from "@/components/TooltipWithIcon/TooltipWithIcon";

import styles from "./ImageWithTooltip.module.scss";
import {
  getCustomCssProps,
  getTooltipPosition,
  TooltipLocationType,
} from "./ImageWithTooltip.utils";
import { Box } from "../Box/Box";

type ImageWithTooltipProps = {
  /**
   * Set className for imageWithTooltip, default is empty.
   */
  className?: string;

  /**
   * Use to set position for tooltip. Available options are topRight and bottomRight
   */
  toolTipPosition?: TooltipLocationType;

  /**
   * Use to set tooltip description for landscape image
   */
  landscapeImageTooltipDescription?: string | React.ReactNode;

  /**
   * Horizontal focal point for landscape image. From 0 to 100. Default is 50.
   */
  lx?: number;

  /**
   * Vertical focal point for landscape image. From 0 to 100. Default is 50.
   */
  ly?: number;

  /**
   * Use to set tooltip description for portrait image
   */
  portraitImageTooltipDescription?: string | React.ReactNode;

  /**
   * Horizontal focal point for portrait image. From 0 to 100. Default is 50.
   */
  px?: number;

  /**
   * Vertical focal point for portrait image. From 0 to 100. Default is 50.
   */
  py?: number;

  /**
   * Use to display all children and ignore orientation rules.
   */
  showAll?: boolean;

  /**
   * Aspect ratio for the image. If not provided, the portrait (or landscape if no portrait)
   * image will be displayed in its original aspect ratio.
   */
  aspectRatio?: number;

  /**
   * @deprecated Use aspectRatio={0} (or not define it) instead
   * For those do not need to support two images or if the containing element does not
   * have defined size either through fixed  or relative dimensions, we just set it as
   * normal container because the container query won't have any dimensions to query against.
   */
  isNormalContainer?: boolean;

  /**
   * Tooltip re-size on screen change
   */
  isPopupFixed?: boolean;
};

const useImageTooltip = (landscape: React.ReactNode, portrait: React.ReactNode) => {
  const [tooltipContent, setTooltipContent] = useState<React.ReactNode | undefined>(portrait);
  const landscapeRef = useRef<HTMLDivElement>(null);
  const portraitRef = useRef<HTMLDivElement>(null);

  const resizeObserver = useMemo(
    () =>
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver((entries) => {
            if (entries[0].target instanceof HTMLDivElement) {
              if (portraitRef.current?.offsetParent) {
                setTooltipContent(portrait);
              } else if (landscapeRef.current?.offsetParent) {
                setTooltipContent(landscape);
              } else {
                setTooltipContent(undefined);
              }
            }
          }),
    [landscape, portrait]
  );

  useEffect(() => {
    const portraitImage = portraitRef.current;
    const landscapeImage = landscapeRef.current;
    portraitImage && resizeObserver?.observe(portraitImage);
    landscapeImage && resizeObserver?.observe(landscapeImage);

    return () => {
      portraitImage && resizeObserver?.unobserve(portraitImage);
      landscapeImage && resizeObserver?.unobserve(landscapeImage);
    };
  }, [resizeObserver]);

  return { tooltipContent, portraitRef, landscapeRef, resizeObserver };
};

const ImageWithTooltipInternal = ({
  className,
  landscapeImage,
  portraitImage,
  toolTipPosition = "bottomRight",
  landscapeImageTooltipDescription,
  lx,
  ly,
  portraitImageTooltipDescription,
  px,
  py,
  showAll,
  aspectRatio = 0,
  isPopupFixed,
}: GroupedChildrenProps<typeof childrenSpec> & ImageWithTooltipProps) => {
  const imageLandscape = Children.toArray(landscapeImage).filter(isValidElement)[0];
  const imagePortrait = Children.toArray(portraitImage).filter(isValidElement)[0];
  const { tooltipContent, portraitRef, landscapeRef } = useImageTooltip(
    landscapeImageTooltipDescription,
    portraitImageTooltipDescription
  );

  if (!imageLandscape && !imagePortrait) {
    return null;
  }

  const customCSSProperties = getCustomCssProps({
    aspectRatio,
    lx,
    ly,
    px,
    py,
  });
  const hasOnlyOne = imagePortrait ? !imageLandscape : imageLandscape;

  const TooltipInternal: React.FC<{ description: React.ReactNode }> = ({ description }) => (
    <Box className={cn(styles.toolTipContainer, styles[toolTipPosition])} px="s" py="xs">
      <TooltipWithIcon
        isPopupFixed={isPopupFixed}
        showPopupOnFocus
        iconColor={TooltipIconColor.ACCENT}
        ariaLabel="image with tooltip"
        pointerPosition={getTooltipPosition(toolTipPosition)}
      >
        {description}
      </TooltipWithIcon>
    </Box>
  );

  return (
    <div
      style={customCSSProperties}
      className={cn(
        styles.imageWithTooltip,
        {
          [styles.aspect]: typeof aspectRatio === "number" && aspectRatio > 0,
        },
        className
      )}
    >
      {typeof aspectRatio !== "number" || !aspectRatio ? (
        <div
          className={imagePortrait ? styles.portraitContainer : styles.landscapeContainer}
          ref={imagePortrait ? portraitRef : landscapeRef}
        >
          {imagePortrait || imageLandscape}
        </div>
      ) : (
        <>
          {/* This is required to maintain aspect ratio */}
          <div className={styles.spaceHolder} />
          <div className={styles.imageWrapper}>
            <Oriented defaultOrientation="portrait" showAll={!!hasOnlyOne || showAll}>
              {imagePortrait && (
                <Oriented.Portrait>
                  <div className={cn(styles.portraitContainer)} ref={portraitRef}>
                    {imagePortrait}
                  </div>
                </Oriented.Portrait>
              )}
              {imageLandscape && (
                <Oriented.Landscape>
                  <div className={cn(styles.landscapeContainer)} ref={landscapeRef}>
                    {imageLandscape}
                  </div>
                </Oriented.Landscape>
              )}
            </Oriented>
          </div>
        </>
      )}
      <TooltipInternal description={tooltipContent} />
    </div>
  );
};

const childrenSpec = {
  LandscapeImage: null,
  PortraitImage: null,
} as const;

const Component: WithGroupedChildrenComponent<typeof childrenSpec, ImageWithTooltipProps> =
  withGroupedChildren({ childrenSpec })(ImageWithTooltipInternal);

/**
 * A helper component which allows content and developers to present images with additional overlay features like tooltips or text. There is a requirement that images on Lexus.com must have the ability for tooltip disclaimer display.
 *
 * ## Usage
 *
 * ```
 * <ImageWithTooltip
    landscapeImageTooltipDescription={landscapeImageTooltipDescription} portraitImageTooltipDescription={portraitImageTooltipDescription} {...args}
  >
      {!noPortrait && (
        <ImageWithTooltip.PortraitImage><img alt="portrait" src={portraitImage} /></ImageWithTooltip.PortraitImage>
      )}
      {!noLandscape && (
        <ImageWithTooltip.LandscapeImage><img alt="Landscape" src={landscapeImage} /></ImageWithTooltip.LandscapeImage>
      )}
  </ImageWithTooltip>
 * ```
 */
export const ImageWithTooltip = Component;
